import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const FREE_SHIPPING_THRESHOLD = 350;

export const StoreProvider = ({ children }) => {
  const sid = getSessionId();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [wishlist, setWishlist] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [quickView, setQuickView] = useState(null);

  const refreshCart = useCallback(async () => {
    try { setCart(await api.getCart(sid)); } catch (e) { if (process.env.NODE_ENV === "development") console.error("refreshCart failed", e); }
  }, [sid]);

  const refreshWishlist = useCallback(async () => {
    try { const w = await api.getWishlist(sid); setWishlist(w.product_ids || []); } catch (e) { if (process.env.NODE_ENV === "development") console.error("refreshWishlist failed", e); }
  }, [sid]);

  useEffect(() => { refreshCart(); refreshWishlist(); }, [refreshCart, refreshWishlist]);

  const addToCart = async (product, quantity = 1) => {
    try {
      const data = await api.addToCart(sid, product.id, quantity);
      setCart(data);
      toast.success(`${product.name} a fost adăugat în coș`);
      setCartOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut adăuga în coș.");
    }
  };

  const updateQuantity = async (product_id, quantity) => {
    try {
      setCart(await api.updateCartItem(sid, product_id, quantity));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cantitate indisponibilă.");
    }
  };

  const removeItem = async (product_id) => {
    setCart(await api.removeCartItem(sid, product_id));
  };

  const toggleWishlist = async (product) => {
    const w = await api.toggleWishlist(sid, product.id);
    setWishlist(w.product_ids || []);
    const added = (w.product_ids || []).includes(product.id);
    toast.success(added ? `${product.name} a fost adăugat la favorite` : `${product.name} a fost eliminat de la favorite`);
  };

  const checkout = async (payload) => {
    const res = await api.checkout({ session_id: sid, ...payload });
    await refreshCart();
    return res;
  };

  const payWithCard = async (payload) => {
    const origin_url = window.location.origin;
    return await api.createStripeCheckout({ session_id: sid, origin_url, ...payload });
  };

  const cartCount = cart.items.reduce((s, i) => s + i.quantity, 0);
  const freeShippingRemaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cart.subtotal);
  const freeShippingProgress = Math.min(100, (cart.subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  const value = {
    sid, cart, cartCount, wishlist,
    cartOpen, setCartOpen, searchOpen, setSearchOpen, wishlistOpen, setWishlistOpen,
    quickView, setQuickView,
    addToCart, updateQuantity, removeItem, toggleWishlist, checkout, payWithCard, refreshCart,
    freeShippingRemaining, freeShippingProgress, FREE_SHIPPING_THRESHOLD,
    isWished: (id) => wishlist.includes(id),
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
